import { type Dispatch, type SetStateAction, useCallback, useEffect, useState } from 'react';
import { type Route, type UserChoice } from '../../types/Forms';
import SwitchButton from '../shared/SwitchButton';
import PassengerCounter from '../shared/PassengerCounter';
import DateDisplay from '../shared/DateDisplay';
import { type DepartureAndArrival } from '../../types/Autocomplete';
import CarTripButton from './CarTripButton';
import { TripService } from '../../services/TripService';
import { useEphemeral } from '../../store/EphemeralContext';
// import { DebugUtils } from '../../tools/DebugUtils';

interface ContainerProps {
  sourceDestination: DepartureAndArrival;
  setSelectedItinerary: Dispatch<SetStateAction<string>>;
  selectedItinerary: string;
  itineraries: Route[];
  setItineraries: Dispatch<SetStateAction<Route[]>>;
  goItineraries: Route[];
  setGoItineraries: Dispatch<SetStateAction<Route[]>>;
  backupReturnItineraries: Route[];
  setBackupReturnItineraries: Dispatch<SetStateAction<Route[]>>;
  sourceDate: number;
}

const tripService = new TripService();

const CarSelection: React.FC<ContainerProps> = ({
  sourceDestination: selectedInputs,
  selectedItinerary,
  setSelectedItinerary,
  itineraries,
  setItineraries,
  goItineraries,
  setGoItineraries,
  backupReturnItineraries,
  setBackupReturnItineraries,
  sourceDate
}) => {
  // Data choice state
  const [userChoice, setUserChoice] = useState<UserChoice>({ passengers: 1 });

  const { switchTrackingMode } = useEphemeral();

  // Dynamically change the user choice state
  const updateUserChoice = useCallback((updates: Partial<UserChoice>) => {
    setUserChoice((current) => {
      return {
        ...current,
        ...updates
      };
    });
  }, []);

  useEffect(() => {
    // set default go itinerary at the beginning
    if (goItineraries.length > 0) {
      const defaultItinerary = goItineraries[0].id;
      if (selectedItinerary === '') {
        setSelectedItinerary(defaultItinerary);
        updateUserChoice({ route: goItineraries[0] });
      }
    }
    // set default return itinerary at the beginning
    if (backupReturnItineraries.length > 0) {
      if (selectedItinerary === '' || userChoice.returnRoute === undefined) {
        const defaultItinerary = backupReturnItineraries[0].id;
        if (userChoice.isReturn ?? false) {
          setSelectedItinerary(defaultItinerary);
        }
        updateUserChoice({ returnRoute: backupReturnItineraries[0] });
      }
    }
  }, [userChoice, goItineraries, setSelectedItinerary, selectedItinerary, backupReturnItineraries, updateUserChoice]);

  /**
   * Change map itineraries
   */
  useEffect(() => {
    // When changing return state
    if (userChoice.isReturn ?? false) {
      setItineraries(backupReturnItineraries);
    } else {
      setItineraries(goItineraries);
    }
    // When changing selected itinerary from return to go without disabling return state
    const existsInBackupReturnItineraries = backupReturnItineraries.some((item) => item.id === selectedItinerary);
    const existsInGoItineraries = goItineraries.some((item) => item.id === selectedItinerary);
    if (existsInBackupReturnItineraries) {
      setItineraries(backupReturnItineraries);
      updateUserChoice({ returnRoute: backupReturnItineraries.find((item) => item.id === selectedItinerary) });
    }
    if (existsInGoItineraries) {
      setItineraries(goItineraries);
      updateUserChoice({ route: goItineraries.find((item) => item.id === selectedItinerary) });
    }
  }, [
    backupReturnItineraries,
    goItineraries,
    selectedItinerary,
    setItineraries,
    setSelectedItinerary,
    updateUserChoice,
    userChoice.isReturn,
    userChoice.route
  ]);

  /**
   * Clear back routes when switched off
   */
  useEffect(() => {
    if (!(userChoice.isReturn ?? false)) {
      setBackupReturnItineraries([]);
      updateUserChoice({ returnRoute: undefined });
    }
  }, [setBackupReturnItineraries, updateUserChoice, userChoice.isReturn]);

  const handleSwitchChange = useCallback(
    (isChecked: boolean) => {
      const prevUserChoice = userChoice;
      if (!isChecked) {
        delete prevUserChoice.returnRoute;
      }
      if (!isChecked) {
        setItineraries([...goItineraries]);
        const defaultItinerary = userChoice.route != null ? userChoice.route.id : goItineraries[0].id;
        setSelectedItinerary(defaultItinerary);
      } else {
        setItineraries([...backupReturnItineraries]);
      }
      prevUserChoice.isReturn = isChecked;
      updateUserChoice(prevUserChoice);
    },
    [backupReturnItineraries, goItineraries, setItineraries, setSelectedItinerary, updateUserChoice, userChoice]
  );

  const handleDateChange = useCallback(
    (newDate: number) => {
      updateUserChoice({ returnDate: newDate });
    },
    [updateUserChoice]
  );

  const navigate = useCallback(() => {
    if ((userChoice.isReturn ?? false) && userChoice.returnRoute === undefined) {
      alert("Vous devez définir l'itinéraire de retour ou le désactiver !");
      return;
    }
    if ((userChoice.isReturn ?? false) && userChoice.returnDate === undefined) {
      alert("Vous devez définir la date de retour ou désactiver l'itinéraire de retour !");
      return;
    }
    switchTrackingMode();
    // const url = `https://www.google.com/maps/dir/?api=1&destination=${selectedInputs.arrival.lat},${selectedInputs.arrival.lon}`;
    // window.open(url, '_blank');
    // const response = await tripService.saveUserTrip(userChoice);
    // alert(response.data.message);
  }, [switchTrackingMode, userChoice.isReturn, userChoice.returnDate, userChoice.returnRoute]);

  return (
    <div id="TakeMyCar" className="flowrideScrollBar p-2">
      {/* Route Choice */}
      <div>
        <h2 className="font-bold text-sm text-ionic-secondary">Quelle route prenez-vous ?</h2>
        {/* Loop on routes */}
        <div className="py-1 overflow-x-auto">
          <div className="flex items-center whitespace-nowrap justify-center">
            <CarTripButton
              type={'no_autoroute_peages'}
              selectedInputs={selectedInputs}
              userChoice={userChoice}
              updateUserChoice={updateUserChoice}
              selectedItinerary={selectedItinerary}
              setSelectedItinerary={setSelectedItinerary}
              setItineraries={setItineraries}
              itineraries={itineraries}
              setGoItineraries={setGoItineraries}
              setBackupReturnItineraries={setBackupReturnItineraries}
            />
            <CarTripButton
              type={'autoroute_peages'}
              selectedInputs={selectedInputs}
              userChoice={userChoice}
              updateUserChoice={updateUserChoice}
              selectedItinerary={selectedItinerary}
              setSelectedItinerary={setSelectedItinerary}
              setItineraries={setItineraries}
              itineraries={itineraries}
              setGoItineraries={setGoItineraries}
              setBackupReturnItineraries={setBackupReturnItineraries}
            />
            <CarTripButton
              type={'eco_friendly'}
              selectedInputs={selectedInputs}
              userChoice={userChoice}
              updateUserChoice={updateUserChoice}
              selectedItinerary={selectedItinerary}
              setSelectedItinerary={setSelectedItinerary}
              setItineraries={setItineraries}
              itineraries={itineraries}
              setGoItineraries={setGoItineraries}
              setBackupReturnItineraries={setBackupReturnItineraries}
            />
          </div>
        </div>
      </div>
      {/* Passengers choice */}
      <div className="mt-1">
        <h2 className="font-bold text-sm text-ionic-secondary">Nombre de passagers acceptés ?</h2>
        <div className="flex items-center justify-center">
          <PassengerCounter userChoice={userChoice} setUserChoice={setUserChoice} maxPassengers={3} />
        </div>
      </div>
      {/* Return route choice */}
      <div className="mt-1">
        <div className="flex items-center">
          <h2 className="font-bold text-sm text-ionic-secondary">Voyage retour</h2>
          <div className="ml-auto">
            <SwitchButton value={(userChoice?.isReturn ?? false) || false} setValue={handleSwitchChange} />
          </div>
        </div>
        <div className="py-1 overflow-x-auto">
          {userChoice != null && (userChoice?.isReturn ?? false) && (
            <div className="flex items-center whitespace-nowrap justify-center mt-2">
              <CarTripButton
                type={'no_autoroute_peages'}
                selectedInputs={selectedInputs}
                userChoice={userChoice}
                updateUserChoice={updateUserChoice}
                isReturn={true}
                selectedItinerary={selectedItinerary}
                setSelectedItinerary={setSelectedItinerary}
                setItineraries={setItineraries}
                itineraries={itineraries}
                setGoItineraries={setGoItineraries}
                setBackupReturnItineraries={setBackupReturnItineraries}
              />
              <CarTripButton
                type={'autoroute_peages'}
                selectedInputs={selectedInputs}
                userChoice={userChoice}
                updateUserChoice={updateUserChoice}
                isReturn={true}
                selectedItinerary={selectedItinerary}
                setSelectedItinerary={setSelectedItinerary}
                setItineraries={setItineraries}
                itineraries={itineraries}
                setGoItineraries={setGoItineraries}
                setBackupReturnItineraries={setBackupReturnItineraries}
              />
              <CarTripButton
                type={'eco_friendly'}
                selectedInputs={selectedInputs}
                userChoice={userChoice}
                updateUserChoice={updateUserChoice}
                isReturn={true}
                selectedItinerary={selectedItinerary}
                setSelectedItinerary={setSelectedItinerary}
                setItineraries={setItineraries}
                itineraries={itineraries}
                setGoItineraries={setGoItineraries}
                setBackupReturnItineraries={setBackupReturnItineraries}
              />
            </div>
          )}
        </div>
      </div>
      {/* Date selector */}
      <div className="mt-2 flex justify-between">
        <h2 className="font-bold text-sm text-ionic-secondary">Date de retour :</h2>
        <DateDisplay timestamp={sourceDate + 4 * 60 * 60 * 1000} onDateChange={handleDateChange} />
      </div>
      {/* Prize message */}
      {/* <div className="flex justify-center items-center mt-4">
        <img src={GreenWinFlashIcon} className="mr-2" />
        <p className="text-ionic-secondary font-bold text-xs">
          Tu vas gagner <span className="text-leaf-green">{userChoice.amount != null ? userChoice.amount : 100} CHF</span> dans ce Trajet
        </p>
      </div> */}
      {/* Action button */}
      <button
        className="mt-2 w-full rounded-md bg-ionic-primary p-2 flex justify-center items-center text-white"
        onClick={() => {
          navigate();
        }}
      >
        {'Navigation'}
      </button>
    </div>
  );
};

export default CarSelection;
